import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './InlineEdit.module.scss';

/* ────────────────────────────────────────────────────────────
 * EditableCell — spreadsheet-cell variant of InlineEdit.
 *
 * Strips the chrome (no pencil affordance, no save/cancel
 * buttons), lays out flush with a grid cell, and exposes
 * dirty-cell tint + save-success pulse hooks for the
 * BaseMenu price grid (MOTION.md §4.11).
 *
 *   <EditableCell
 *     value={row.price}
 *     onCommit={(v) => updatePrice(row.id, v)}
 *     dirty={isDirty}
 *     justSaved={savedTick === row.id}
 *     type="number"
 *     align="right"
 *   />
 *
 * Keyboard contract:
 *   Enter → commit + move down (caller handles focus move)
 *   Tab   → commit + move right (caller handles focus move)
 *   Esc   → cancel
 * ──────────────────────────────────────────────────────────── */

const EditableCell = ({
  value,
  onCommit,
  onKeyNav,
  type,
  align,
  dirty,
  justSaved,
  errored,
  disabled,
  formatDisplay,
  className,
  ariaLabel,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  const commit = useCallback(() => {
    if (draft !== value) onCommit(draft);
    setEditing(false);
  }, [draft, value, onCommit]);

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); onKeyNav?.('down'); }
    else if (e.key === 'Tab') {
      // When a grid host wires up onKeyNav, it owns focus movement —
      // suppress the browser's default Tab so the manual focus jump
      // doesn't overshoot the destination cell.
      if (onKeyNav) { e.preventDefault(); commit(); onKeyNav(e.shiftKey ? 'left' : 'right'); }
      else { commit(); }
    }
    else if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(value);
      setEditing(false);
    }
  };

  const cls = [
    styles.cell,
    align === 'right' ? styles.cellRight : '',
    align === 'center' ? styles.cellCenter : '',
    editing ? styles.cellEditing : '',
    dirty ? styles.cellDirty : '',
    justSaved ? styles.cellSaved : '',
    errored ? styles.cellErrored : '',
    disabled ? styles.disabled : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  const display = formatDisplay ? formatDisplay(value) : value;

  if (!editing) {
    return (
      <div
        className={cls}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel || 'Edit cell'}
        onClick={() => !disabled && setEditing(true)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'F2') {
            e.preventDefault();
            setEditing(true);
          }
        }}
      >
        {display}
      </div>
    );
  }

  return (
    <div className={cls}>
      <input
        ref={inputRef}
        className={styles.cellInput}
        type={type}
        value={draft ?? ''}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKey}
        onBlur={commit}
        aria-label={ariaLabel || 'Edit cell'}
      />
    </div>
  );
};

EditableCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCommit: PropTypes.func.isRequired,
  onKeyNav: PropTypes.func,
  type: PropTypes.string,
  align: PropTypes.oneOf(['left', 'right', 'center']),
  dirty: PropTypes.bool,
  justSaved: PropTypes.bool,
  errored: PropTypes.bool,
  disabled: PropTypes.bool,
  formatDisplay: PropTypes.func,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

EditableCell.defaultProps = {
  value: '',
  onKeyNav: null,
  type: 'text',
  align: 'left',
  dirty: false,
  justSaved: false,
  errored: false,
  disabled: false,
  formatDisplay: null,
  className: '',
  ariaLabel: '',
};

export default EditableCell;
